import { useEffect, useState, useRef } from "react";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  useLoadScript
} from "@react-google-maps/api";
import { MAPS_API_KEY } from "../../../../components/mapKey";
import { decryptToken } from "../../../../Utlis/Encryption";
import { BASE_URL } from "../../../../components/BaseUrl";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import CustomButton from "../../../../components/CustomButton";
import CloseIcon from "@mui/icons-material/Close";

const InActivePins = ({
  selectedClientId,
  onClearClientId,
  filters,
  page,
  limit,
  search
}) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: MAPS_API_KEY,
  });

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const rawEncryptedToken = localStorage.getItem("token");
  const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;

  const mapRef = useRef(null);
  const hasFitted = useRef(false);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [fullMapOpen, setFullMapOpen] = useState(false);

  useEffect(() => {
    setLoading(true);

    const params = new URLSearchParams({
      page: page + 1,
      limit,
      sort_by: "created_at",
      sort_order: "DESC",
      status: "inactive",
    });

    if (filters?.verification_status && filters.verification_status !== "all") {
      params.append("verification_status", filters.verification_status);
    }
    if (filters?.start_date) params.append("start_date", filters.start_date);
    if (filters?.end_date) params.append("end_date", filters.end_date);
    if (filters?.country) params.append("country", filters.country);
    if (filters?.state) params.append("state", filters.state);
    if (filters?.city) params.append("city", filters.city);
    if (filters?.subscription_status) params.append("subscription_status", filters.subscription_status);
    if (filters?.trial_end_date_from) params.append("trial_end_date_from", filters.trial_end_date_from);
    if (filters?.trial_end_date_to) params.append("trial_end_date_to", filters.trial_end_date_to);
    if (filters?.subscription_start_date_from) params.append("subscription_start_date_from", filters.subscription_start_date_from);
    if (filters?.subscription_start_date_to) params.append("subscription_start_date_to", filters.subscription_start_date_to);
    if (search) params.append("search", search);

    fetch(`${BASE_URL}/company-admins?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data?.data?.company_admins) {
          const valid = data.data.company_admins.filter(
            (c) => c.latitude && c.longitude
          );
          setClients(valid);
          hasFitted.current = false; // reset for new data
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token, filters, page, limit, BASE_URL, search]);

  const filteredClients = selectedClientId
    ? clients.filter((c) => c.id === selectedClientId)
    : clients;

  // ✅ fitBounds (same as your working pattern)
  useEffect(() => {
    if (!mapRef.current || filteredClients.length === 0) return;
    if (hasFitted.current) return;

    const bounds = new window.google.maps.LatLngBounds();

    filteredClients.forEach((client) => {
      bounds.extend({
        lat: parseFloat(client.latitude),
        lng: parseFloat(client.longitude),
      });
    });

    mapRef.current.fitBounds(bounds);
    hasFitted.current = true;
  }, [filteredClients]);

  // 🔴 red pin
  const iconInactive = {
    url: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
    scaledSize: new window.google.maps.Size(25, 41),
    anchor: new window.google.maps.Point(12, 41),
  };

  useEffect(() => {
  if (!mapRef.current) return;

  setTimeout(() => {
    window.google.maps.event.trigger(mapRef.current, "resize");
  }, 300);
}, [filteredClients, fullMapOpen]);
useEffect(() => {
  hasFitted.current = false;
}, [filteredClients]);
  return (
    <>
      <div style={{ height: "55vh", width: "100%", position: "relative", borderRadius: "12px", overflow: "hidden" }}>

        {selectedClientId && (
          <Box
            sx={{
              position: "absolute",
              top: 32,
              right: 12,
              zIndex: 2000,
              backgroundColor: "#fff",
              borderRadius: "6px",
              padding: "4px 8px",
              cursor: "pointer",
            }}
            onClick={() => onClearClientId && onClearClientId()}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </Box>
        )}

        {loading && (
          <Box sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1000
          }}>
            <CircularProgress />
          </Box>
        )}

        {isLoaded && (
          <GoogleMap
            // mapContainerStyle={{ height: "100%", width: "100%" }}
            // onLoad={(map) => (mapRef.current = map)}
              mapContainerStyle={{ height: "100%", width: "100%" }}
  onLoad={(map) => (mapRef.current = map)}
center={{ lat: 37.0902, lng: -95.7129 }} // USA center
  zoom={5} 
          >
            {filteredClients.map((client) => (
              <Marker
                key={client.id}
                position={{
                  lat: parseFloat(client.latitude),
                  lng: parseFloat(client.longitude),
                }}
                icon={iconInactive}
                onClick={() => setSelectedMarker(client)}
              />
            ))}

            {selectedMarker && (
              <InfoWindow
                position={{
                  lat: parseFloat(selectedMarker.latitude),
                  lng: parseFloat(selectedMarker.longitude),
                }}
                onCloseClick={() => setSelectedMarker(null)}
              >
                <div style={{ fontSize: "12px", minWidth: "200px" }}>
                  <strong>{selectedMarker.company_name || "N/A"}</strong><br />
                  {selectedMarker.email}<br />
                  {selectedMarker.country}
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        )}
      </div>

      <CustomButton
        onClick={() => setFullMapOpen(true)}
        label={t("fullMap")}
        size="small"
        sx={{ position: "absolute", top: 12, right: 12 }}
      />

      {fullMapOpen && (
        <Box sx={{ position: "fixed", inset: 0, backgroundColor: "#fff", zIndex: 2000 }}>
          <Box onClick={() => setFullMapOpen(false)} sx={{ position: "absolute", top: 16, right: 16 }}>✕</Box>

          {isLoaded && (
            <GoogleMap
              mapContainerStyle={{ height: "100%", width: "100%" }}
              onLoad={(map) => (mapRef.current = map)}
            >
              {filteredClients.map((client) => (
                <Marker
                  key={client.id}
                  position={{
                    lat: parseFloat(client.latitude),
                    lng: parseFloat(client.longitude),
                  }}
                  icon={iconInactive}
                />
              ))}
            </GoogleMap>
          )}
        </Box>
      )}
    </>
  );
};

export default InActivePins;